import React, { type ReactNode } from 'react';
import isArray from 'lodash/isArray';
import { Text } from '@atlaskit/primitives/compiled';
import type { Flag } from '@atlassian/jira-flags';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { GroupContextField } from '../types.tsx';
import messages from './messages.tsx';
import type { CreatedIssue, OnSortChangeFn, FireAnalyticsFn, RankFieldTerms } from './types.tsx';

export const fieldValueContainsGroupContextValue = (
	createdIssue: CreatedIssue,
	groupContextField: GroupContextField,
	fieldId: string,
) => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find((field) => field?.fieldId === fieldId);

	let fieldValue: string | string[] | number | null;

	switch (issueField?.__typename) {
		case 'JiraStatusField':
			fieldValue = issueField.status?.statusId ?? null;
			break;
		case 'JiraPriorityField':
			fieldValue = issueField.priority?.priorityId ?? null;
			break;
		case 'JiraSingleSelectUserPickerField':
			fieldValue = issueField.user?.accountId ?? null;
			break;
		case 'JiraSingleSelectField':
			fieldValue = issueField.fieldOption?.optionId ?? null;
			break;
		case 'JiraRadioSelectField':
			fieldValue = issueField.selectedOption?.optionId ?? null;
			break;
		case 'JiraCheckboxesField':
		case 'JiraMultipleSelectField':
			fieldValue =
				issueField.selectedOptions?.edges
					?.map((edge) => edge?.node?.optionId)
					.filter((optionId): optionId is string => !!optionId) ?? null;
			break;
		case 'JiraSprintField':
			fieldValue =
				issueField.selectedSprintsConnection?.edges
					?.map((edge) => edge?.node?.sprintId)
					.filter((sprintId): sprintId is string => !!sprintId) ?? null;
			break;
		case 'JiraNumberField':
			fieldValue = issueField.number ?? null;
			break;
		case 'JiraLabelsField':
			fieldValue =
				issueField.selectedLabelsConnection?.edges
					?.map((edge) => edge?.node?.name)
					.filter((name): name is string => !!name) ?? null;
			break;
		case 'JiraMultipleVersionPickerField':
			fieldValue =
				issueField.selectedVersionsConnection?.edges
					?.map((edge) => edge?.node?.versionId)
					.filter((versionId): versionId is string => !!versionId) ?? null;
			break;
		case 'JiraSingleVersionPickerField':
			fieldValue = issueField.version?.versionId ?? null;
			break;
		default:
			fieldValue = null;
	}
	if (!isArray(fieldValue)) {
		return fieldValue === groupContextField.fieldValue;
	}

	if (fieldValue.length === 0) {
		return groupContextField.fieldValue === null;
	}

	return (
		typeof groupContextField.fieldValue === 'string' &&
		fieldValue?.includes(groupContextField.fieldValue)
	);
};

export const getStatusName = (createdIssue: CreatedIssue) => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find(
		(field) => field?.fieldId === STATUS_TYPE,
	);

	return issueField?.status?.name;
};

const Bold = (chunk: ReactNode) => <Text as="strong">{chunk}</Text>;
export const getIssueCreateSuccessFlag = ({
	issueKey,
	statusName,
}: {
	issueKey: string;
	statusName?: string | null;
}): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-success',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-success',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: statusName
		? [messages.workItemChangedStatusDescription, { issueKey, statusName, Bold }]
		: undefined,
	actions: [
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});

export const getIssueCreateRerankingPromptSuccessFlag = ({
	issueKey,
	onSortChange,
	rankFieldTerms,
	fireApplyRankingAnalytics,
	fireViewWorkItemUIAnalytics,
}: {
	issueKey: string;
	onSortChange?: OnSortChangeFn;
	rankFieldTerms?: RankFieldTerms;
	fireApplyRankingAnalytics: FireAnalyticsFn;
	fireViewWorkItemUIAnalytics: FireAnalyticsFn;
}): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-reranking-prompt-success',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-reranking-prompt-success',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: [messages.rerankingPromptDescription, { issueKey, Bold }],
	actions: [
		{
			content: messages.applyRanking,
			onClick: () => {
				fireApplyRankingAnalytics();
				rankFieldTerms?.jqlTerm &&
					onSortChange?.(
						rankFieldTerms.jqlTerm,
						'ASC',
						rankFieldTerms.aliases && fg('jsc_list_reranking_ast_refactor')
							? rankFieldTerms.aliases
							: undefined,
					);
			},
		},
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
			onClick: () => {
				fireViewWorkItemUIAnalytics();
			},
		},
	],
});

export const getIssueCreateHiddenFlag = ({ issueKey }: { issueKey: string }): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-hidden',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-hidden',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: [
		fg('jira-issue-terminology-refresh-m3')
			? messages.workItemHiddenDescription
			: messages.issueHiddenDescription,
		{ issueKey, Bold },
	],
	actions: [
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});
